import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import rest, { getData } from 'util/Api';
import { useSelector, useDispatch } from 'react-redux';
import { userViewAvatar, userViewLoaded } from 'redux/userView/selectors';
import { selectGlobals } from 'redux/globals/selectors';
import { authProfile } from 'redux/auth/selectors';
import useUserview from 'redux/userView/index';
import useUsers from 'redux/users';
import { actions as authActions } from 'redux/auth/slice';
import { actions } from 'redux/userView/slice';
import { actions as usersActions } from 'redux/users/slice';
import { notification } from 'antd';
import User from 'routes/app/user';
import { IMAGE_REGEX, PHONE_REGEX } from 'constants/defaultValues';
import { ROLES } from 'constants/roles';
import API from 'constants/api';
import * as Yup from 'yup';
import { usersItemsSelector } from 'redux/users/selectors';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[\p{L}\p{N}\s'-]+$/u, 'No se permiten caracteres especiales')
    .required('Es necesario indicar un nombre'),
  lastName: Yup.string()
    .matches(/^[\p{L}\p{N}\s'-]+$/u, 'No se permiten caracteres especiales')
    .required('Es necesario indicar un apellido'),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Formato de telefono invalido.')
    .required('Es necesario indicar el número de teléfono'),
  email: Yup.string()
    .email('El formato de correo no es correcto')
    .required('Es necesario indicar el correo electrónico'),
});

const disabledInputs = {
  userRoleID: true,
};

const defaultValues = {
  name: '',
  lastName: '',
  phone: null,
  email: '',
};

const UserProfile = () => {
  useUserview();
  useUsers();
  const globals = useSelector(selectGlobals);
  const dispatch = useDispatch();
  const profile = useSelector(authProfile);
  const loaded = useSelector(userViewLoaded);
  const avatar = useSelector(userViewAvatar);
  const users = useSelector(usersItemsSelector);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [currentCity, setCurrentCity] = useState(null);
  const [cities, setCities] = useState([]);
  const currentRole = ROLES.filter((role) => role.value === profile.userRoleID);
  const history = useHistory();

  useEffect(() => {
    return () => {
      dispatch(actions.resetValues());
    };
  }, [dispatch]);

  useEffect(() => {
    const getCities = async () => {
      dispatch(actions.setLoaded(false));
      const citiesResponse = await getData(
        `${API.globals.cities}/${currentCountry}`,
      );
      setCities(citiesResponse);
      if (citiesResponse.length > 0) {
        setCurrentCity(citiesResponse[0].id);
      } else {
        setCurrentCity(undefined);
      }
      dispatch(actions.setLoaded(true));
    };
    if (currentCountry) {
      getCities();
    }
  }, [currentCountry, dispatch]);

  useEffect(() => {
    dispatch(actions.resetValues());
    dispatch(usersActions.initial());
    dispatch(actions.setLoaded(false));
    const getCitiesAndLoadProfile = async () => {
      const citiesResponse = await getData(
        `${API.globals.cities}/${profile.countryID}`,
      );
      setCities(citiesResponse);
      setInitialValues(profile);
      dispatch(actions.setLoaded(true));
    };
    dispatch(actions.setAvatar({ data: profile.avatar }));
    getCitiesAndLoadProfile();
  }, [profile, dispatch]);

  useEffect(() => {
    setCurrentUser(users.find((item) => item.id === profile.id));
  }, [users]);

  const handleSubmit = async (values) => {
    const { id: paqueryPointID } = profile.paquerypoint;
    const payload = {
      Avatar: avatar.name,
      AvatarImg: avatar.name ? avatar.data.replace(IMAGE_REGEX, '') : undefined,
      CityID: values.cityID,
      CountryID: values.countryID,
      Email: values.email,
      ID: values.id,
      LastName: values.lastName,
      LogisticOperatorID: values.logisticOperatorID || null,
      MarketplaceId: currentUser.marketplaceID,
      Mobile: values.phone,
      Name: values.name,
      TermsAndConditions: currentUser.termsAndConditions,
      UserRoleID: values.userRoleID,
      paqueryPointID,
    };
    try {
      const response = await rest.post(API.user.update, payload);
      if (response.status === 400) {
        throw new Error(response.data.message);
      } else {
        notification.success({
          message: 'Actualización de perfil',
          description: 'Tu perfil ha sido actualizado correctamente',
        });
        dispatch(authActions.reloadProfile());
        setTimeout(() => {
          history.goBack();
        }, 1000);
      }
    } catch (error) {
      notification.error({
        message: 'Error al actualizar el perfil',
        description: error.message,
      });
      console.error(error);
    }
  };
  return (
    <User
      loaded={loaded}
      avatar={avatar}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      countries={globals.countries}
      cities={cities}
      roles={currentRole}
      currentCity={currentCity}
      currentCountry={currentCountry}
      setCurrentCountry={setCurrentCountry}
      isProfile
      disabledInputs={disabledInputs}
    />
  );
};

export default UserProfile;
